
import type { Application } from '@/types/database.types';

/**
 * Checks if a section has all its required fields filled
 * @param section The section name to check
 * @param data The application data
 * @returns boolean indicating if all required fields are present
 */
export const hasRequiredFields = (section: string, data?: Partial<Application>): boolean => {
  if (!data) return false;
  
  switch (section) {
    case 'Basic Information':
      return !!(data.first_name && data.last_name && data.email && data.phone);
    case 'Entity Details':
      // Simplified requirements for Entity Details
      return true;
    case 'Insurance Details':
      // Mark section as complete if ANY carrier information has been provided or explicitly set
      // This includes empty strings as valid values (user cleared the field)
      const medicalCarrierTouched = data.current_medical_carrier !== undefined && 
                                   data.current_medical_carrier !== null;
                         
      const workersCompTouched = data.current_workers_comp_carrier !== undefined && 
                                data.current_workers_comp_carrier !== null;
                            
      const ancillaryCarrierTouched = data.current_ancillary_carrier !== undefined && 
                                     data.current_ancillary_carrier !== null;
      
      // Handle desired carriers sections too
      let desiredMedicalTouched = false;
      if (data.desired_medical_carriers) {
        if (typeof data.desired_medical_carriers === 'string') {
          // Handle string representation of array
          const desiredMedicalValue = data.desired_medical_carriers as string;
          if (desiredMedicalValue.startsWith('[')) {
            try {
              const parsedCarriers = JSON.parse(desiredMedicalValue);
              desiredMedicalTouched = Array.isArray(parsedCarriers);
            } catch (e) {
              desiredMedicalTouched = false;
            }
          } else {
            desiredMedicalTouched = true;
          }
        } else {
          desiredMedicalTouched = Array.isArray(data.desired_medical_carriers);
        }
      }
      
      const desiredAncillaryTouched = Array.isArray(data.desired_ancillary_carriers);
      
      // For debugging
      console.log("Insurance validation - Carrier data status:", { 
        medicalCarrierTouched, 
        workersCompTouched, 
        ancillaryCarrierTouched,
        desiredMedicalTouched,
        desiredAncillaryTouched,
        values: {
          medical: data.current_medical_carrier,
          workersComp: data.current_workers_comp_carrier,
          ancillary: data.current_ancillary_carrier,
          desiredMedical: data.desired_medical_carriers,
          desiredAncillary: data.desired_ancillary_carriers
        }
      });
      
      // Mark section as complete if ANY carrier information has been provided
      // This includes explicitly setting fields to empty strings (a valid choice)
      return medicalCarrierTouched || workersCompTouched || ancillaryCarrierTouched ||
             desiredMedicalTouched || desiredAncillaryTouched;
      
    case 'Staffing & Payroll':
    case 'Payroll Schedule': // Added this case to handle both naming conventions
      // Updated validation logic for Payroll Schedule section
      // Check if pay schedule type and dates are set
      const hasPayScheduleBasics = !!(data.pay_schedule_type && data.next_check_date);
      
      // Log the payroll data for debugging
      console.log("Payroll Schedule validation data:", {
        pay_schedule_type: data.pay_schedule_type,
        pay_period_start: data.pay_period_start,
        pay_period_end: data.pay_period_end,
        next_check_date: data.next_check_date,
        has_bonuses: data.has_bonuses,
        has_commission: data.has_commission
      });
      
      // Consider the section complete if the basic pay schedule info is provided
      return hasPayScheduleBasics;
    
    case 'Operational Details':
      return !!(data.business_activities && data.naics_code);
      
    // Add dedicated validation for Medical Questionnaire section
    case 'Medical Questionnaire':
      // Check if any of the medical condition fields have been set to true or false
      // We'll consider the section complete if ANY medical condition field has been explicitly set
      return data.has_kidney_disease !== undefined ||
             data.has_diabetes !== undefined ||
             data.has_liver_disease !== undefined ||
             data.has_neural_disorder !== undefined ||
             data.has_cardiovascular !== undefined ||
             data.has_respiratory_disease !== undefined ||
             data.has_musculoskeletal !== undefined ||
             data.has_transplants !== undefined ||
             data.has_cancer !== undefined ||
             data.has_hiv_aids !== undefined ||
             data.has_mental_health !== undefined ||
             data.has_nervous_system !== undefined ||
             data.has_other_conditions !== undefined ||
             data.has_premature_birth !== undefined;
             
    // Add Workers Comp Assessment validation
    case 'Workers Comp Assessment':
      // Consider the section complete if any of these key fields have been set
      return data.has_over_road_trucking !== undefined ||
             data.has_cannabis_hemp_contact !== undefined ||
             data.has_elevated_workplace_risk !== undefined ||
             data.has_company_aircraft !== undefined ||
             data.has_marine_operations !== undefined ||
             data.has_heavy_machinery !== undefined ||
             data.has_recent_wc_claims !== undefined ||
             data.has_recent_layoffs !== undefined ||
             data.has_planned_layoffs !== undefined ||
             data.has_foreign_travel !== undefined ||
             data.has_epli_insurance !== undefined ||
             data.has_standalone_epli_policy !== undefined ||
             data.current_workers_comp_carrier !== undefined;
             
    case 'Document Upload':
      // Check if ANY of the required document files have been uploaded
      // We only need at least one document to mark this section as complete
      console.log("Document upload validation - checking files:", {
        employee_census: data.employee_census_file,
        dependent_census: data.dependent_census_file,
        workers_comp_declaration: data.workers_comp_declaration_file,
        health_insurance_renewal: data.health_insurance_renewal_file,
        other_documents: data.other_documents
      });
      
      return !!(
        data.employee_census_file || 
        data.dependent_census_file || 
        data.workers_comp_declaration_file || 
        data.workers_comp_loss_runs_file ||
        data.health_insurance_renewal_file ||
        data.tax_exempt_status_file ||
        data.other_documents
      );
    case 'PEO Preferences':
      // Check if needs_guidance has been set (either true or false) to indicate the section was visited
      return data.needs_guidance !== null && data.needs_guidance !== undefined;
    default:
      return false;
  }
};

